export const EMERGENCY_ACTIVATION_METHODS = {
  MANUAL_PANIC: 'manualPanic',
  SAFETY_CHECK: 'safetyCheck',
  EXTERNAL: 'external',
} as const;

export const EMERGENCY_INCIDENT_STATES = {
  ACTIVE: 'active',
  CANCELED_FALSE_ALARM: 'canceledFalseAlarm',
  RESOLVED: 'resolved',
} as const;

export const EMERGENCY_RESPONDER_STATES = {
  INVITED: 'invited',
  JOINED: 'joined',
  ACCEPTED: 'accepted',
  EN_ROUTE: 'enRoute',
  ARRIVED: 'arrived',
  REASSIGNED: 'reassigned',
  RELEASED: 'released',
} as const;

export const EMERGENCY_NOTIFICATION_STATUSES = {
  QUEUED: 'queued',
  PROVIDER_ACCEPTED: 'providerAccepted',
  SENT: 'sent',
  DELIVERED: 'delivered',
  VIEWED: 'viewed',
  ACCEPTED: 'accepted',
} as const;

export type EmergencyActivationMethod =
  (typeof EMERGENCY_ACTIVATION_METHODS)[keyof typeof EMERGENCY_ACTIVATION_METHODS];
export type EmergencyIncidentState =
  (typeof EMERGENCY_INCIDENT_STATES)[keyof typeof EMERGENCY_INCIDENT_STATES];
export type EmergencyResponderState =
  (typeof EMERGENCY_RESPONDER_STATES)[keyof typeof EMERGENCY_RESPONDER_STATES];
export type EmergencyNotificationStatus =
  (typeof EMERGENCY_NOTIFICATION_STATUSES)[keyof typeof EMERGENCY_NOTIFICATION_STATUSES];
export type EmergencySafetyCheckSatisfactionMode =
  | 'manualConfirmation'
  | 'either';
/**
 * Why a safety-check session is armed. The app arms and disarms monitoring itself:
 * `always` while the worker is signed in without a work journey, `workJourney`
 * while their journey is active (`referenceId` is the journey). `explicit` and
 * `shift` only appear on sessions and incidents created before that model.
 */
export type EmergencyMonitoringContext = 'always' | 'workJourney' | 'explicit' | 'shift';
export const EMERGENCY_MONITORING_CONTEXTS: EmergencyMonitoringContext[] = [
  'always',
  'workJourney',
  'explicit',
  'shift',
];
/** `degraded` is legacy: the backend no longer tracks a device heartbeat. */
export type EmergencyMonitoringState = 'active' | 'degraded' | 'closed';
export type EmergencyMonitoringWarningStage = 'normal' | 'standard' | 'fullScreen' | 'overdue';
/**
 * Outbound lifecycle events an integration can subscribe to.
 *
 * Values must match `EmergencyIntegrationEventType` in
 * `services/src/schemas/TEmergency.ts` — that is what
 * `parseEmergencyIntegrationInput` validates and what the outbox stores
 * and delivers as `eventType`.
 */
export type EmergencyIntegrationEvent =
  | 'incidentActivated'
  | 'escalationAdvanced'
  | 'responderUpdated'
  | 'incidentResolved'
  | 'locationUpdated'
  | 'notificationUpdated';

export type EmergencyCommandOperation =
  | 'createType'
  | 'updateType'
  | 'deleteType'
  | 'saveEnrollment'
  | 'activatePanic'
  | 'startMonitoring'
  | 'endMonitoring'
  | 'recordManualSatisfaction'
  | 'recordActivitySatisfaction'
  | 'cancelFalseAlarm'
  | 'acceptPrimary'
  | 'joinSupport'
  | 'enRoute'
  | 'arrived'
  | 'release'
  | 'reassign'
  | 'assignPrimary'
  | 'addNote'
  | 'resolve'
  | 'acknowledgeDelivered'
  | 'acknowledgeViewed'
  | 'publishLocation'
  | 'createIntegration'
  | 'updateIntegration'
  | 'deleteIntegration'
  | 'rotateIntegration'
  | 'listIntegrations'
  | 'listIntegrationDeliveries'
  | 'getCapabilities';

export interface EmergencyCommandUserSummary {
  id: string;
  displayName: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  [field: string]: unknown;
}

export interface EmergencyCommandGroup {
  id: string;
  divId: string;
  name: string;
  users: EmergencyCommandUserSummary[];
  [field: string]: unknown;
}

export interface EmergencyMembers {
  users: EmergencyCommandUserSummary[];
  groups: EmergencyCommandGroup[];
}

export interface EmergencyEscalationLevel {
  level: number;
  delaySeconds: number;
  responders: {
    memberIds: string[];
    groupIds: string[];
  };
}

export interface EmergencySafetyCheckConfig {
  satisfactionMode: EmergencySafetyCheckSatisfactionMode;
  intervalSeconds: number;
  standardWarningAfterSeconds: number;
  fullScreenWarningAfterSeconds: number;
  emergencyAfterSeconds: number;
  standardWarningText: string;
  fullScreenWarningText: string;
}

export interface EmergencyManualPanicConfig {
  preActivationCancelWindowSeconds: number;
}

export type EmergencyTypeSettings =
  | {
      activationMethod: 'manualPanic';
      manualPanic: EmergencyManualPanicConfig;
    }
  | {
      activationMethod: 'safetyCheck';
      safetyCheck: EmergencySafetyCheckConfig;
    }
  | {
      activationMethod: 'external';
    };

export interface EmergencyType {
  id: string;
  organizationId: string;
  divisionId: string;
  name: string;
  description: string;
  active: boolean;
  settings: EmergencyTypeSettings;
  escalationLevels: EmergencyEscalationLevel[];
  /**
   * Safety-check type saved before app-driven arming (it still stores monitoring
   * contexts). Workers cannot be monitored until an admin saves it again.
   */
  requiresResave: boolean;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export type EmergencyTypeInput = Omit<
  EmergencyType,
  'id' | 'organizationId' | 'createdAt' | 'updatedAt' | 'requiresResave'
>;

export interface EmergencyEnrollment {
  id: string;
  organizationId: string;
  divisionId: string;
  userId: string;
  emergencyTypeId: string;
  active: boolean;
  source: 'configuration' | 'manual';
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface EmergencyEnrollmentSelection {
  divisionId: string;
  emergencyTypeId: string;
  memberIds: string[];
  groupIds: string[];
}

export interface EmergencyResponder {
  assignmentId: string;
  userId: string;
  displayName: string;
  state: EmergencyResponderState;
  primary: boolean;
  stateUpdatedAt: Date | null;
}

export type EmergencyIncidentSubject =
  | {
      kind: 'linkedWorker';
      userId: string;
      displayName: string;
      phone?: string;
    }
  | {
      kind: 'externalPerson';
      displayName: string;
      externalId?: string;
      phone?: string;
      email?: string;
      context?: string;
      source: string;
      sourceQuality: 'verified' | 'trusted' | 'unverified';
    };

export interface EmergencyMonitoringOrigin {
  sessionId: string;
  context: EmergencyMonitoringContext;
  referenceId?: string;
}

export interface EmergencyIncidentClosure {
  closedAt: Date | null;
  closedBy?: string;
  reason?: string;
  outcome?: string;
  summary?: string;
}

export interface EmergencyIncident {
  id: string;
  organizationId: string;
  divisionId: string;
  emergencyTypeId: string;
  emergencyTypeName: string;
  activationMethod: EmergencyActivationMethod;
  state: EmergencyIncidentState;
  activatedAt: Date | null;
  endedAt: Date | null;
  closure?: EmergencyIncidentClosure;
  createdAtDevice: Date | null;
  subject: EmergencyIncidentSubject;
  source: {
    type: string;
    label: string;
    externalId?: string;
  };
  monitoringOrigin?: EmergencyMonitoringOrigin;
  escalation: {
    currentLevel: number;
    currentLevelName: string;
    nextDeadlineAt: Date | null;
  };
  primaryAssignmentId?: string;
  response: {
    primary: EmergencyResponder | null;
    support: EmergencyResponder[];
  };
  freshness: {
    lastActivityAt: Date | null;
    lastLocationAt: Date | null;
    stale: boolean;
  };
  allowedCommands: EmergencyCommandOperation[];
}

export interface EmergencyMonitoringSession {
  id: string;
  emergencyTypeId: string;
  emergencyTypeName: string;
  enrollmentId: string;
  divisionId: string;
  userId: string;
  workerName: string;
  context: EmergencyMonitoringContext;
  referenceId?: string;
  satisfactionMode: EmergencySafetyCheckSatisfactionMode;
  state: EmergencyMonitoringState;
  heartbeatIntervalSeconds: number;
  lastSatisfiedAt: Date | null;
  standardWarningAt: Date | null;
  fullScreenWarningAt: Date | null;
  emergencyAt: Date | null;
  incidentId?: string;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface EmergencyTimelineEntry {
  id: string;
  incidentId: string;
  type: string;
  message: string;
  actorId?: string;
  actorRole?: string;
  actorName: string;
  /** Free-form external context, when the event carries a `context` payload. */
  contextText?: string;
  createdAt: Date | null;
}

export interface EmergencyNotification {
  id: string;
  incidentId: string;
  channel: 'push';
  recipientId: string;
  recipientName: string;
  escalationLevel: number;
  status: EmergencyNotificationStatus;
  queuedStatus: 'pending' | 'queued';
  providerStatus: 'pending' | 'providerAccepted' | 'sent';
  deliveryStatus: 'pending' | 'delivered';
  viewStatus: 'pending' | 'viewed';
  acceptanceStatus: 'pending' | 'accepted';
  queuedAt: Date | null;
  providerAcceptedAt: Date | null;
  sentAt: Date | null;
  deliveredAt: Date | null;
  viewedAt: Date | null;
  acceptedAt: Date | null;
}

export interface EmergencyLocation {
  id: string;
  incidentId: string;
  userId: string;
  latitude: number;
  longitude: number;
  accuracyMeters: number | null;
  capturedAt: Date | null;
  receivedAt: Date | null;
  /** `tracking` marks a fallback from the general live tracking, not an incident sample. */
  source?: 'incident' | 'tracking';
}

export interface EmergencyIntegration {
  id: string;
  organizationId: string;
  divisionId: string;
  emergencyTypeId: string;
  name: string;
  provider: 'api' | 'webhook';
  endpoint: string;
  enabled: boolean;
  subscribedEvents: EmergencyIntegrationEvent[];
  endpointStatus: 'pending' | 'active' | 'disabled' | 'error';
  connectionStatus: 'unknown' | 'connected' | 'disconnected' | 'degraded';
  credentialStatus: 'active' | 'rotating' | 'revoked';
  credentialHint: string;
  configVersion: number;
  lastDeliveryAt: Date | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface EmergencyIntegrationInput {
  divisionId: string;
  emergencyTypeId: string;
  name: string;
  provider: 'api' | 'webhook';
  endpoint: string;
  enabled: boolean;
  subscribedEvents: EmergencyIntegrationEvent[];
}

export interface EmergencyIntegrationCommandResult {
  id: string;
  credential?: string;
}

export type EmergencyIntegrationDeliveryState =
  | 'pending'
  | 'processing'
  | 'published'
  | 'failed'
  | 'deadLetter';

export interface EmergencyIntegrationDeliveryAttempt {
  attempt: number;
  startedAt: Date | null;
  completedAt: Date | null;
  status: 'pending' | 'succeeded' | 'failed';
  responseCode?: number;
  errorCode?: string;
}

export interface EmergencyIntegrationDelivery {
  id: string;
  integrationId: string;
  incidentId: string;
  divisionId: string;
  type: EmergencyIntegrationEvent;
  sequence: number;
  state: EmergencyIntegrationDeliveryState;
  attempts: number;
  deliveryAttempts: EmergencyIntegrationDeliveryAttempt[];
  nextAttemptAt: Date | null;
  lastAttemptAt: Date | null;
  publishedAt: Date | null;
  deadLetteredAt: Date | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface EmergencyIntegrationDeliveryPage {
  deliveries: EmergencyIntegrationDelivery[];
  nextCursorId?: string;
}

export interface EmergencyTypeFirestoreRecord {
  id: string;
  divisionId?: string;
  name?: string;
  description?: string;
  active?: boolean;
  settings?: Record<string, unknown>;
  escalationLevels?: unknown;
  createdAt?: unknown;
  updatedAt?: unknown;
  uid?: string;
}

export type EmergencyTypeCommandRecord = {
  id?: string;
  divisionId: string;
  name: string;
  description: string;
  active: boolean;
  escalationLevels: Array<{
    level: number;
    delaySeconds: number;
    memberIds: string[];
    groupIds: string[];
  }>;
} & (
  | {
      activationMethod: typeof EMERGENCY_ACTIVATION_METHODS.MANUAL_PANIC;
      manualPanic: EmergencyManualPanicConfig;
    }
  | {
      activationMethod: typeof EMERGENCY_ACTIVATION_METHODS.SAFETY_CHECK;
      safetyCheck: EmergencySafetyCheckConfig;
    }
  | {
      activationMethod: typeof EMERGENCY_ACTIVATION_METHODS.EXTERNAL;
    }
);

export interface EmergencyEnrollmentFirestoreRecord {
  id: string;
  divisionId?: string;
  userId?: string;
  emergencyTypeId?: string;
  active?: boolean;
  source?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
  uid?: string;
}

export interface EmergencyActivationSnapshotFirestoreRecord {
  emergencyTypeId?: string;
  emergencyTypeName?: string;
  emergencyTypeUpdatedAt?: unknown;
  divisionId?: string;
  settings?: Record<string, unknown>;
  escalationLevels?: unknown;
}

export interface EmergencyIncidentFirestoreRecord {
  id: string;
  divisionId?: string;
  workerId?: string;
  uid?: string;
  enrollmentId?: string;
  subject?: Record<string, unknown>;
  state?: string;
  activation?: EmergencyActivationSnapshotFirestoreRecord;
  usersWithAccess?: unknown;
  primaryAssignmentId?: string;
  currentEscalationLevel?: number;
  nextEscalationAt?: unknown;
  nextEscalationDeadlineAt?: unknown;
  acceptedAt?: unknown;
  canceledAt?: unknown;
  closedAt?: unknown;
  resolvedAt?: unknown;
  closure?: Record<string, unknown>;
  cancellation?: Record<string, unknown>;
  resolution?: unknown;
  createdAt?: unknown;
  createdAtDevice?: unknown;
  updatedAt?: unknown;
  monitoringOrigin?: Record<string, unknown>;
}

export interface EmergencyMonitoringSessionFirestoreRecord {
  id: string;
  emergencyTypeId?: string;
  enrollmentId?: string;
  divisionId?: string;
  userId?: string;
  scope?: Record<string, unknown>;
  satisfactionMode?: string;
  state?: string;
  heartbeatIntervalSeconds?: number;
  heartbeatFreshnessSeconds?: number;
  lastSatisfiedAt?: unknown;
  lastHeartbeatAt?: unknown;
  heartbeatFreshUntil?: unknown;
  standardWarningAt?: unknown;
  fullScreenWarningAt?: unknown;
  emergencyAt?: unknown;
  incidentId?: string;
  degradedAt?: unknown;
  createdAt?: unknown;
  updatedAt?: unknown;
}

export interface EmergencyAssignmentFirestoreRecord {
  id: string;
  incidentId?: string;
  divisionId?: string;
  responder?: Record<string, unknown>;
  state?: string;
  primary?: boolean;
  notificationReceiptId?: string;
  acceptedAt?: unknown;
  enRouteAt?: unknown;
  arrivedAt?: unknown;
  reassignedToAssignmentId?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
  uid?: string;
}

export interface EmergencyTimelineFirestoreRecord {
  id: string;
  incidentId?: string;
  divisionId?: string;
  type?: string;
  actorId?: string;
  actorDisplayName?: string;
  actorRole?: string;
  actor?: Record<string, unknown>;
  payload?: Record<string, unknown>;
  createdAt?: unknown;
}

export interface EmergencyNotificationFirestoreRecord {
  id: string;
  incidentId?: string;
  divisionId?: string;
  recipientId?: string;
  recipientDisplayName?: string;
  recipient?: Record<string, unknown>;
  escalationLevel?: number;
  status?: string;
  queuedAt?: unknown;
  providerAcceptedAt?: unknown;
  sentAt?: unknown;
  deliveredAt?: unknown;
  viewedAt?: unknown;
  acceptedAt?: unknown;
  createdAt?: unknown;
  updatedAt?: unknown;
}

export interface EmergencyLocationFirestoreRecord {
  id: string;
  incidentId?: string;
  divisionId?: string;
  userId?: string;
  latitude?: number;
  longitude?: number;
  accuracyMeters?: number;
  capturedAt?: unknown;
  receivedAt?: unknown;
  sampledAt?: unknown;
  createdAt?: unknown;
}

export interface EmergencyIntegrationViewRecord {
  id: string;
  organizationId: string;
  divisionId: string;
  emergencyTypeId: string;
  name: string;
  provider: 'api' | 'webhook';
  endpoint: string;
  enabled: boolean;
  subscribedEvents: EmergencyIntegrationEvent[];
  endpointStatus: EmergencyIntegration['endpointStatus'];
  connectionStatus: EmergencyIntegration['connectionStatus'];
  credentialStatus: EmergencyIntegration['credentialStatus'];
  credential: {
    secretHint: string;
  };
  configVersion: number;
  lastDeliveryAt?: unknown;
  createdAt: unknown;
  updatedAt: unknown;
}

export interface EmergencyIntegrationDeliveryViewRecord {
  id: string;
  integrationId: string;
  incidentId: string;
  divisionId: string;
  type: EmergencyIntegrationEvent;
  sequence: number;
  state: EmergencyIntegrationDeliveryState;
  attempts: number;
  deliveryAttempts: Array<{
    attempt: number;
    startedAt: unknown;
    completedAt?: unknown;
    status: 'pending' | 'succeeded' | 'failed';
    responseCode?: number;
    errorCode?: string;
  }>;
  nextAttemptAt: unknown;
  lastAttemptAt?: unknown;
  publishedAt?: unknown;
  deadLetteredAt?: unknown;
  createdAt: unknown;
  updatedAt: unknown;
}

export interface EmergencyCommandResponse {
  operation: EmergencyCommandOperation;
  id?: string;
  incidentId?: string;
  integrationId?: string;
  secret?: string;
  credential?: string;
  allowedCommands?: EmergencyCommandOperation[];
  integrations?: EmergencyIntegrationViewRecord[];
  deliveries?: EmergencyIntegrationDeliveryViewRecord[];
  nextDeliveryCursorId?: string;
  replayed?: boolean;
}

export interface EmergencyIncidentCommandInput {
  organizationId: string;
  divisionId: string;
  incidentId: string;
}

export interface ReassignEmergencyResponderInput extends EmergencyIncidentCommandInput {
  assignmentId: string;
  responderId: string;
}

export interface AssignPrimaryEmergencyResponderInput extends EmergencyIncidentCommandInput {
  responderId: string;
}

export interface AddEmergencyNoteInput extends EmergencyIncidentCommandInput {
  text: string;
}

export interface CancelFalseAlarmInput extends EmergencyIncidentCommandInput {
  reason: string;
}

export interface ResolveEmergencyInput extends EmergencyIncidentCommandInput {
  reason: string;
  outcome: string;
}
